"use client";

import { useMemo, useState } from "react";
import { X, Plug } from "lucide-react";
import { SlideDrawer } from "@/components/ui/slide-drawer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { capabilityColors } from "@/lib/capability-colors";
import { CapabilityListPanel } from "@/components/capability-list/CapabilityListPanel";
import type { CapabilityVisibility } from "@/types/capability-types";
import { normalizeCapabilityVisibility } from "@/lib/capability-visibility";

export interface ProviderOption {
    name: string;
    description: string | null;
    tags?: string[];
    app_key?: string;
    visibility?: string;
    created_by?: string;
    organization_id?: string;
    access_source?: string;
}

const SORT_OPTIONS = [
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
];

export type VisibilityScope = CapabilityVisibility | "all";

interface ProviderPickerDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    providers: ProviderOption[];
    selectedProviderRef: string;
    onSelect: (providerName: string) => void;
    loading?: boolean;
    /** Current user ID (subject) for User filter */
    currentUserId?: string;
    /** Current organization ID for Organization filter */
    currentOrgId?: string;
}

const VISIBILITY_SCOPE_OPTIONS: { key: VisibilityScope; label: string }[] = [
    { key: "all", label: "All" },
    { key: "user", label: "User" },
    { key: "organization", label: "Organization" },
    { key: "application", label: "Application" },
    { key: "account", label: "Account" },
    { key: "global", label: "Global" },
];

export function ProviderPickerDrawer({
    isOpen,
    onClose,
    providers,
    selectedProviderRef,
    onSelect,
    loading = false,
    currentUserId: _currentUserId = "",
    currentOrgId: _currentOrgId = "",
}: ProviderPickerDrawerProps) {
    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState<string>("name");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [visibilityScope, setVisibilityScope] = useState<VisibilityScope>("all");

    const allTags = useMemo(() => {
        const tagSet = new Set<string>();
        for (const p of providers) {
            if (p.tags) {
                for (const t of p.tags) tagSet.add(t);
            }
        }
        return Array.from(tagSet).sort();
    }, [providers]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const filteredProviders = useMemo(() => {
        let list = providers.filter((p) => {
            if (
                visibilityScope !== "all" &&
                p.access_source !== "entitled" &&
                normalizeCapabilityVisibility(p.visibility) !== visibilityScope
            ) {
                return false;
            }
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                if (
                    !p.name.toLowerCase().includes(q) &&
                    !(p.description?.toLowerCase().includes(q) ?? false)
                ) {
                    return false;
                }
            }
            if (selectedTags.size > 0 && p.tags) {
                if (!p.tags.some((t) => selectedTags.has(t))) return false;
            } else if (selectedTags.size > 0 && !p.tags?.length) {
                return false;
            }
            return true;
        });
        const cmp = (a: ProviderOption, b: ProviderOption) => {
            if (sortField === "name") {
                return a.name.localeCompare(b.name);
            }
            const ad = (a.description ?? "").toLowerCase();
            const bd = (b.description ?? "").toLowerCase();
            return ad.localeCompare(bd);
        };
        list = [...list].sort((a, b) => {
            const r = cmp(a, b);
            return sortDirection === "asc" ? r : -r;
        });
        return list;
    }, [providers, searchQuery, selectedTags, sortField, sortDirection, visibilityScope]);

    const showEmptySource = !loading && providers.length === 0;

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
                            <div className="flex items-center gap-3">
                                <div
                                    className={cn(
                                        "flex h-10 w-10 items-center justify-center rounded-lg",
                                        capabilityColors.provider.bgLight,
                                        capabilityColors.provider.text
                                    )}
                                >
                                    <Plug size={20} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">
                                        Select Provider
                                    </h2>
                                    <p className="text-sm text-slate-500">
                                        Choose the provider capability for this model
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onClose}
                                className="h-8 w-8 p-0"
                            >
                                <X size={16} />
                            </Button>
                        </div>

                        {showEmptySource ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center flex-1">
                                <Plug size={32} className="text-slate-300 mb-3" />
                                <p className="text-sm text-slate-500">
                                    No providers available
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                    Create a provider capability in this application first
                                </p>
                            </div>
                        ) : (
                            <CapabilityListPanel<ProviderOption>
                                items={filteredProviders}
                                sortOptions={SORT_OPTIONS}
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                searchQuery={searchQuery}
                                onSearchChange={setSearchQuery}
                                searchPlaceholder="Search providers by name or description..."
                                allTags={allTags}
                                selectedTags={selectedTags}
                                onToggleTag={(tag) => {
                                    setSelectedTags((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(tag)) next.delete(tag);
                                        else next.add(tag);
                                        return next;
                                    });
                                }}
                                onClearAllTags={() => setSelectedTags(new Set())}
                                onSelectAllTags={() => setSelectedTags(new Set(allTags))}
                                isLoading={loading}
                                loadingMessage="Loading providers..."
                                emptyMessage="No providers found."
                                extraToolbar={
                                    <div className="flex items-center rounded-md border border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] p-1">
                                        {VISIBILITY_SCOPE_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                onClick={() => setVisibilityScope(opt.key)}
                                                className={cn(
                                                    "flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                                    visibilityScope === opt.key
                                                        ? "bg-[hsl(var(--list-controls-sort-active-bg))] text-[hsl(var(--list-controls-sort-active-fg))] shadow-sm"
                                                        : "text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]"
                                                )}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                }
                                toolbarClassName="px-12 py-4 border-b border-slate-100 shrink-0"
                                listControlsVariant="solid"
                                contentClassName="flex-1 overflow-y-auto px-24 py-6"
                                gridClassName="grid grid-cols-3 gap-3"
                                renderCard={(provider) => {
                                    const providerFqcn = provider.app_key
                                        ? `${provider.app_key}/${provider.name}`
                                        : provider.name;
                                    const isSelected =
                                        selectedProviderRef === providerFqcn ||
                                        selectedProviderRef === provider.name;
                                    return (
                                        <Card
                                            key={provider.name}
                                            className={cn(
                                                "relative border-2 p-3 transition-all duration-200 cursor-pointer min-h-[88px]",
                                                "border-l-4",
                                                isSelected
                                                    ? "border-green-500 bg-green-50/50 ring-2 ring-green-200"
                                                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md"
                                            )}
                                            style={{
                                                borderLeftColor: isSelected ? undefined : capabilityColors.provider.hex,
                                            }}
                                            onClick={() => {
                                                const fqcn = provider.app_key
                                                    ? `${provider.app_key}/${provider.name}`
                                                    : provider.name;
                                                onSelect(fqcn);
                                                onClose();
                                            }}
                                        >
                                            <div className="flex flex-row items-start gap-3">
                                                <div
                                                    className={cn(
                                                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                                                        capabilityColors.provider.bgLight,
                                                        capabilityColors.provider.text
                                                    )}
                                                >
                                                    <Plug size={16} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                                            provider
                                                        </span>
                                                        {provider.access_source === "entitled" && (
                                                            <Badge
                                                                variant="secondary"
                                                                className="h-4 px-1.5 text-[9px] font-medium"
                                                            >
                                                                Subscribed
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <span className="block text-sm font-bold truncate text-slate-900">
                                                        {provider.name}
                                                    </span>
                                                    {provider.description && (
                                                        <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight block mt-0.5">
                                                            {provider.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </Card>
                                    );
                                }}
                            />
                        )}
        </SlideDrawer>
    );
}
